import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_HIDDEN_ATTR,
  SITE_HEADER_OUTER_HEIGHT_PX,
  SITE_HEADER_TOP_VAR,
  SITE_SCROLL_PADDING_TOP_PX,
} from "src/domains/shared/constants/styles/site";
import { REVEAL_IN_ATTR } from "../siteKit/motion/reveal";
import { SITE_SECTION_ATTR } from "../siteKit/SiteSection";
import { SITE_MOTION_ATTR, SITE_MOTION_READY_ATTR } from "./siteRoutes";

const MOTION = `[${SITE_MOTION_ATTR}]`;
const NOT_READY = `[${SITE_MOTION_ATTR}]:not([${SITE_MOTION_READY_ATTR}])`;
const SECTION = `[${SITE_SECTION_ATTR}]`;
/** 화면에 들어온 등장 효과 요소 */
const IN = `[${REVEAL_IN_ATTR}]`;
/** 스크립트가 늦거나 실패해도 이 시간 뒤에는 숨긴 내용을 보인다 */
const REVEAL_FALLBACK_DELAY = "3s";
/** 머리말이 미끄러져 사라지는 시간(siteHeaderStyles 의 transform 전환과 같게) */
const HEADER_SLIDE = ".3s ease";

/**
 * 머리말 자리 변수를 길이로 등록한다 — 등록해야 값이 바뀔 때 transition 이 걸려 홈 첫 장면이 머리말과 같은 박자로 걷힌다.
 * 등록을 지원하지 않는 브라우저는 한 번에 바뀐다. 같은 이름을 두 번 등록하면 예외라 한 번만 등록한다.
 */
export function registerSiteHeaderTopProperty() {
  if (typeof CSS === "undefined" || typeof CSS.registerProperty !== "function")
    return;
  try {
    CSS.registerProperty({
      name: SITE_HEADER_TOP_VAR,
      syntax: "<length>",
      inherits: true,
      initialValue: `${SITE_HEADER_OUTER_HEIGHT_PX}px`,
    });
  } catch {
    // 이미 등록됨
  }
}

/**
 * 사이트 틀이 붙어 있는 동안만 들어가는 전역 규칙(시안 site.css 의 문서 수준 규칙).
 *
 * - 앱 전역 `* { font-family: sans-serif; line-height: normal }` 이 모든 요소에 직접 걸려 상속을 끊으므로, 사이트 안에서는
 *   특이도 0(`:where`)으로 상속을 되살린다 — 컴포넌트 클래스(특이도 1)는 언제나 이 규칙을 이긴다.
 * - 등장 효과의 숨김 상태는 `<html data-site-motion>` 일 때만 걸린다(문서 머리 스크립트가 첫 칠 전에 단다).
 */
export const SITE_GLOBAL_STYLES = {
  html: {
    scrollPaddingTop: `${SITE_SCROLL_PADDING_TOP_PX}px`,
    WebkitTextSizeAdjust: "100%",
    [SITE_HEADER_TOP_VAR]: `${SITE_HEADER_OUTER_HEIGHT_PX}px`,
    transition: `${SITE_HEADER_TOP_VAR} ${HEADER_SLIDE}`,
  },
  // 머리말이 스크롤로 숨은 동안 — 머리말 아래에 붙는 틀(홈 첫 장면)이 그 자리까지 걷힌다
  [`html[${SITE_HEADER_HIDDEN_ATTR}]`]: { [SITE_HEADER_TOP_VAR]: "0px" },
  ":where([data-site] *)": {
    fontFamily: "inherit",
    letterSpacing: "inherit",
    lineHeight: "inherit",
  },
  // 단추·입력 칸은 브라우저 기본처럼 글자 간격을 물려받지 않는다(시안과 같게 — 간격을 정한 단추만 그 값)
  ":where([data-site]) :where(button, input, textarea, select)": {
    letterSpacing: "normal",
  },
  ":where([data-site]) img": {
    display: "block",
    maxWidth: "100%",
    height: "auto",
    WebkitTouchCallout: "none",
  },
  ":where([data-site]) a": { color: "inherit", textDecoration: "none" },
  ":where([data-site]) button": {
    font: "inherit",
    color: "inherit",
    background: "none",
    border: 0,
    padding: 0,
    cursor: "pointer",
  },
  ":where([data-site]) :is(img,svg,a,video,canvas)": { WebkitUserDrag: "none" },
  // 제목의 일본어·크메르어 구(SiteTitle) — 그 안에서는 줄을 바꾸지 않고, 한 줄보다 길 때만 안에서 접힌다
  ":where([data-site]) [data-phrase]": { display: "inline-block" },
  // 이어지는 구역은 위 여백을 겹치지 않는다(시안 `.sec + .sec`) — 구역 클래스보다 강해야 해서 특이도 2
  [`${SECTION} + ${SECTION}`]: { paddingTop: 0 },
  ":where([data-site]) :focus-visible": {
    outline: `2px solid ${SITE_COLOR.blue}`,
    outlineOffset: "2px",
    borderRadius: "4px",
  },

  [`${MOTION} [data-reveal="up"]`]: {
    opacity: 0,
    transform: "translate3d(0,28px,0)",
    transition: `opacity .8s ${SITE_EASE.out},transform .8s ${SITE_EASE.out}`,
    transitionDelay: "calc(var(--i,0) * 70ms)",
  },
  [`${MOTION} [data-reveal="up"]${IN}`]: { opacity: 1, transform: "none" },
  [`${MOTION} [data-reveal="img"]`]: {
    clipPath: "inset(6% 4% 6% 4% round var(--r-img))",
    transition: `clip-path 1.1s ${SITE_EASE.out}`,
  },
  [`${MOTION} [data-reveal="img"] > img, ${MOTION} [data-reveal="img"] > picture img`]:
    {
      transform: "scale(1.12)",
      transition: `transform 1.4s ${SITE_EASE.out}`,
    },
  [`${MOTION} [data-reveal="img"]${IN}`]: {
    clipPath: "inset(0 0 0 0 round var(--r-img))",
  },
  [`${MOTION} [data-reveal="img"]${IN} > img, ${MOTION} [data-reveal="img"]${IN} > picture img`]:
    {
      transform: "none",
    },
  [`${MOTION} [data-reveal="words"] .w`]: {
    display: "inline-block",
    overflow: "hidden",
    verticalAlign: "top",
    paddingBottom: ".08em",
    marginBottom: "-.08em",
  },
  [`${MOTION} [data-reveal="words"] .w > span`]: {
    display: "inline-block",
    transform: "translate3d(0,105%,0)",
    transition: `transform .9s ${SITE_EASE.out}`,
    transitionDelay: "calc(var(--i,0) * 45ms)",
  },
  [`${MOTION} [data-reveal="words"]${IN} .w > span`]: { transform: "none" },
  [`${MOTION} [data-reveal="fade"]`]: {
    opacity: 0,
    transition: "opacity 1s ease",
  },
  [`${MOTION} [data-reveal="fade"]${IN}`]: { opacity: 1 },
  [`${NOT_READY} [data-reveal]:not(${IN}), ${NOT_READY} [data-reveal="words"]:not(${IN}) .w > span`]:
    {
      animation: `siteRevealFallback 0s linear ${REVEAL_FALLBACK_DELAY} forwards`,
    },

  "@keyframes siteRevealFallback": {
    to: { opacity: 1, transform: "none", clipPath: "none" },
  },
  "@keyframes sitePop": {
    from: { opacity: 0, transform: "scale(.96) translateY(-4px)" },
    to: { opacity: 1, transform: "none" },
  },
  "@keyframes siteRise": {
    from: { opacity: 0, transform: "translateY(22px)" },
    to: { opacity: 1, transform: "none" },
  },
  "@keyframes siteSettle": {
    from: { transform: "scale(1.07)" },
    to: { transform: "none" },
  },

  "@media (prefers-reduced-motion: reduce)": {
    html: { transition: "none" },
    "[data-site] *, [data-site] *::before, [data-site] *::after": {
      animationDuration: ".001ms !important",
      animationIterationCount: "1 !important",
      transitionDuration: ".001ms !important",
      scrollBehavior: "auto !important",
    },
  },
} as const;
